import { useMemo, useState } from 'react';
import { customReportApi } from '../../../api/customReportApi';
import { REPORT_TRANSLATION_LANGUAGES } from '../../../constants/reportTranslationLanguages';
import { apiError, DEFAULT_LANGUAGE, isRunning, languageName, listOrEmpty } from '../utils/customReportUtils';
import { useIntervalTask } from './useIntervalTask';

const TRANSLATION_POLL_MS = 5000;

/**
 * The languages of the open report version: the original, its translations, the one on
 * screen, and polling while a translation runs.
 */
export const useReportLanguages = ({ scope, scopeId, selectedId, selectedIdRef, completed, setOutput, setError, onNotify }) => {
  const [languages, setLanguages] = useState([]);
  const [sourceLanguage, setSourceLanguage] = useState(DEFAULT_LANGUAGE);
  const [activeLanguage, setActiveLanguage] = useState(DEFAULT_LANGUAGE);
  const [translating, setTranslating] = useState(false);
  const poll = useIntervalTask();
  // The API takes no language for the original text, only for a translation.
  const translationLang = activeLanguage === sourceLanguage ? null : activeLanguage;

  const missingLanguages = useMemo(() => {
    const present = new Set(languages.map((item) => item.code));
    return REPORT_TRANSLATION_LANGUAGES.filter(
      (item) => item.code !== sourceLanguage && !present.has(item.code)
    );
  }, [languages, sourceLanguage]);

  const applyLanguages = (page) => {
    setLanguages(listOrEmpty(page.languages));
    setSourceLanguage(page.source_language || DEFAULT_LANGUAGE);
    setActiveLanguage(page.language || page.source_language || DEFAULT_LANGUAGE);
  };

  const resetLanguages = () => {
    poll.stop();
    setLanguages([]);
    setActiveLanguage(DEFAULT_LANGUAGE);
  };

  const selectLanguage = async (code) => {
    if (!selectedId || code === activeLanguage) return;
    setError('');
    try {
      const lang = code === sourceLanguage ? null : code;
      const full = await customReportApi.get(scope, scopeId, selectedId, lang);
      setOutput(full.output || null);
      applyLanguages(full);
      setActiveLanguage(code);
    } catch (err) {
      setError(apiError(err, 'Failed to switch language'));
    }
  };

  /** Poll until the queued translation finishes, then open it. */
  const pollTranslation = (code) => {
    poll.start(async () => {
      try {
        const list = await customReportApi.getLanguages(scope, scopeId, selectedIdRef.current);
        setLanguages(listOrEmpty(list));
        const entry = (list || []).find((item) => item.code === code);
        if (!entry || isRunning(entry)) return;
        poll.stop();
        setTranslating(false);
        if (entry.status === 'COMPLETED') await selectLanguage(code);
        else setError(entry.error_message || `Translation to ${languageName(code)} failed`);
      } catch (err) {
        poll.stop();
        setTranslating(false);
        setError(apiError(err, 'Failed to check translation status'));
      }
    }, TRANSLATION_POLL_MS);
  };

  /** After opening a version: keep following a translation that is still running. */
  const resumeTranslation = (pageLanguages) => {
    const pending = (pageLanguages || []).find(isRunning);
    poll.stop();
    setTranslating(Boolean(pending));
    if (pending) pollTranslation(pending.code);
  };

  const translate = async (code) => {
    if (!selectedId || !completed) return;
    setError('');
    setTranslating(true);
    try {
      const page = await customReportApi.translate(scope, scopeId, selectedId, code);
      applyLanguages({ ...page, language: activeLanguage });
      onNotify?.({
        type: 'info',
        title: 'Translation started',
        message: `Translating this report into ${languageName(code)}.`,
      });
      pollTranslation(code);
    } catch (err) {
      setTranslating(false);
      setError(apiError(err, 'Failed to start translation'));
    }
  };

  const deleteTranslation = async (code) => {
    if (!selectedId) return;
    if (!window.confirm(`Remove the ${languageName(code)} translation?`)) return;
    setError('');
    try {
      await customReportApi.deleteTranslation(scope, scopeId, selectedId, code);
      if (code === activeLanguage) await selectLanguage(sourceLanguage);
      const list = await customReportApi.getLanguages(scope, scopeId, selectedId);
      setLanguages(listOrEmpty(list));
    } catch (err) {
      setError(apiError(err, 'Failed to remove translation'));
    }
  };

  return {
    languages,
    sourceLanguage,
    activeLanguage,
    translationLang,
    translating,
    missingLanguages,
    applyLanguages,
    resetLanguages,
    resumeTranslation,
    stopPolling: poll.stop,
    selectLanguage,
    translate,
    deleteTranslation,
  };
};
